import { useEffect, useRef, useCallback } from 'react';
import { 
  Chart, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  LineController, 
  Title, 
  Tooltip, 
  Filler, 
  Legend 
} from 'chart.js';
import type { ChartDataPoint } from '../../lib/chartUtils';
import { Card, CardContent } from '../ui/card';
import { cn } from '../../lib/utils';

export type MultiLineChartDataset = {
  deviceId: string;
  deviceName: string;
  color: string;
  data: ChartDataPoint[];
};

type MultiLineChartProps = {
  title: string;
  datasets: MultiLineChartDataset[];
  height?: number;
  className?: string;
  yAxisLabel?: string;
  showLegend?: boolean;
};

// Register only needed components (reduces bundle size)
Chart.register(CategoryScale, LinearScale, PointElement, LineController, LineElement, Title, Tooltip, Filler, Legend);

export function MultiLineChart({ 
  title, 
  datasets,
  height = 250,
  className,
  yAxisLabel = '%',
  showLegend = true
}: MultiLineChartProps) {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  // Safe chart destruction
  const destroyChart = useCallback(() => {
    if (chartInstance.current) {
      try {
        chartInstance.current.destroy();
        chartInstance.current = null;
      } catch (error) {
        console.warn('Error destroying chart:', error);
      }
    }
  }, []);

  const createChart = useCallback(() => {
    if (!chartRef.current) return;

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    const existing = Chart.getChart(chartRef.current);
    if (existing) {
      try { existing.destroy(); } catch {}
    }

    const validDatasets = datasets.filter(ds => ds.data && Array.isArray(ds.data) && ds.data.length > 0);

    if (validDatasets.length === 0) {
      destroyChart();
      try {
        chartInstance.current = new Chart(ctx, {
          type: 'line',
          data: { labels: [], datasets: [] },
          options: { responsive: true, maintainAspectRatio: false }
        });
      } catch (error) {
        console.warn('No valid data points for chart and failed to render empty chart:', error);
      }
      return;
    }

    try {
      destroyChart();

      // Collect all unique time labels from all datasets
      const allTimeLabels = new Set<string>();
      validDatasets.forEach(ds => {
        ds.data.forEach((point: ChartDataPoint) => {
          if (point.time) {
            allTimeLabels.add(point.time);
          }
        });
      });

      // Convert to sorted array
      const timeLabels = Array.from(allTimeLabels).sort((a, b) => {
        // Sort by timestamp if available, otherwise by string comparison
        const datasetA = validDatasets.find(ds => ds.data.find(p => p.time === a));
        const datasetB = validDatasets.find(ds => ds.data.find(p => p.time === b));
        const pointA = datasetA?.data.find(p => p.time === a);
        const pointB = datasetB?.data.find(p => p.time === b);
        
        if (pointA && pointB && 'timestamp' in pointA && 'timestamp' in pointB) {
          return new Date(pointA.timestamp as string).getTime() - new Date(pointB.timestamp as string).getTime();
        }
        return a.localeCompare(b);
      });

      // Debug logging
      console.log(`[${title}] Number of devices:`, validDatasets.length);
      console.log(`[${title}] Time labels:`, timeLabels);
      console.log(`[${title}] Sample device data:`, validDatasets[0]);

      const chartDatasets = validDatasets.map((ds) => {
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, `${ds.color}33`);
        gradient.addColorStop(1, `${ds.color}00`);

        // Map data to match time labels
        const dataValues = timeLabels.map(label => {
          const dataPoint = ds.data.find((point: ChartDataPoint) => point.time === label);
          return dataPoint?.value ?? null;
        });

        console.log(`[${title}] Device ${ds.deviceName} has ${dataValues.length} data points:`, dataValues);

        return {
          label: ds.deviceName,
          data: dataValues,
          borderColor: ds.color,
          backgroundColor: gradient,
          borderWidth: 2,
          tension: 0.4,
          pointRadius: dataValues.filter((value) => value != null).length <= 2 ? 3 : 0,
          pointHoverRadius: 5,
          pointHoverBorderWidth: 2,
          fill: true,
          spanGaps: true, // Connect points even if there are null values
          segment: {
            borderColor: ds.color,
          }
        };
      });

      chartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: timeLabels,
          datasets: chartDatasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: {
              display: showLegend,
              position: 'bottom',
              labels: {
                usePointStyle: true,
                boxWidth: 8,
                font: {
                  size: 11,
                  family: 'Arial',
                },
                padding: 10,
              }
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              backgroundColor: '#1e293b',
              titleColor: '#f8fafc',
              bodyColor: '#e2e8f0',
              borderColor: '#334155',
              borderWidth: 1,
              padding: 10,
              callbacks: {
                title: function(context) {
                  return context[0]?.label || '';
                },
                label: function(context) {
                  const value = context.parsed.y;
                  if (value === null) return '';

                  const dataset = context.dataset;
                  const formatted = isFinite(value) ? value.toFixed(2) : '0';
                  return `${dataset.label}: ${formatted} ${yAxisLabel}`;
                }
              }
            }
          },
          scales: {
            x: {
              grid: { 
                display: true, 
                color: '#e5e7eb',
              },
              border: {
                display: true,
              },
              ticks: { 
                color: '#64748b', 
                font: { size: 9 },
                maxRotation: 45,
                minRotation: 45,
                autoSkip: true,
                autoSkipPadding: 10,
                maxTicksLimit: timeLabels.length > 100 ? 15 : (timeLabels.length > 50 ? 12 : 10)
              }
            },
            y: {
              beginAtZero: true,
              max: yAxisLabel === '%' ? 100 : undefined,
              grid: { 
                color: '#e5e7eb',
              },
              border: {
                display: true,
              },
              ticks: {
                color: '#64748b',
                font: { size: 11 },
                callback: function(value) {
                  const num = typeof value === 'string' ? Number(value) : value as number;
                  return isFinite(num) ? `${num.toFixed(0)} ${yAxisLabel}` : `0 ${yAxisLabel}`;
                }
              }
            }
          }
        }
      });

    } catch (error) {
      console.error('Error creating multi-line chart:', error);
    }
  }, [datasets, title, height, destroyChart, yAxisLabel, showLegend]);

  useEffect(() => {
    createChart();
    return destroyChart;
  }, [createChart, destroyChart]);

  // Handle empty data
  if (!datasets || datasets.length === 0) {
    return (
      <Card className={cn("overflow-hidden", className)}>
        <CardContent className="p-5">
          <h3 className="text-base font-medium text-neutral-dark mb-4">{title}</h3>
          <div 
            style={{ height: `${height}px` }} 
            className="flex items-center justify-center bg-gray-50 rounded-lg"
          >
            <div className="text-center text-gray-500">
              <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p>No data available</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("overflow-hidden", className)}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-medium text-neutral-dark">{title}</h3>
        </div>
        <div style={{ height: `${height}px` }} className="relative">
          <canvas ref={chartRef} />
        </div>
      </CardContent>
    </Card>
  );
}
